import { type Key, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Modal,
  Radio,
  Select,
  Upload,
  message,
  Popconfirm,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import type { RcFile } from 'antd/es/upload';
import styles from './index.module.scss';
import LOCALS from '../../../../commons/locals';
import i18n from '../../../../i18n';
import { OmsRecycleOrderDetail } from 'types/oms';
import { batchFetchPaymentVoucher } from 'apis/oms';
import { thousands } from 'utils/tools';
import {
  OrderType,
  PAYMENT_ACCOUNT_OPTIONS,
} from 'constants/RecyclingConsignment';
import {
  extractClipboardImageFiles,
  fileToUploadFile,
} from './clipboard-image';
import { getBase64, mergeImages } from './image-utils';

// 提取订单金额计算逻辑，避免重复
const getOrderAmount = (order: OmsRecycleOrderDetail): number => {
  const { finalSalePrice, finalRecyclePrice } =
    order?.omsRecycleOrderItem || {};
  const orderType = order?.omsRecycleOrder?.type;

  if (orderType === 1) return finalSalePrice || 0;
  if (orderType === 2) return finalRecyclePrice || 0;
  return 0;
};

interface Props {
  open: boolean;
  onClose: () => void;
  selectedOrderIds: Key[];
  selectedOrders: OmsRecycleOrderDetail[];
  onSuccess?: () => void;
}

const BatchPaymentVoucherModal = ({
  open,
  onClose,
  selectedOrderIds,
  selectedOrders,
  onSuccess,
}: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [voucherFileList, setVoucherFileList] = useState<UploadFile[]>([]);
  const selectedPaymentMethod = Form.useWatch('settlementType', form);

  useEffect(() => {
    if (!open) return;

    const onPaste = (e: ClipboardEvent) => {
      const images = extractClipboardImageFiles(e);
      if (images.length === 0) return;

      e.preventDefault();

      // 追加到现有列表，而不是替换
      const newFiles = images.map((img) => fileToUploadFile(img));
      setVoucherFileList((prev) => [...prev, ...newFiles]);
      message.success(
        `已从剪贴板粘贴 ${images.length} 张图片${
          images.length > 1 ? '（提交时会自动合成为一张）' : ''
        }`,
      );
    };

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open]);

  // 合并所有 Set 收集逻辑，减少重复遍历
  const orderStats = useMemo(() => {
    const stats = {
      currencies: new Set<string>(),
      settlementTypes: new Set<number>(),
      storeIds: new Set<string | number>(),
      customerIds: new Set<string | number>(),
    };

    selectedOrders.forEach((order) => {
      const { currency, settlementType, storeId, memberId } =
        order?.omsRecycleOrder || {};

      if (currency) stats.currencies.add(currency);
      if (settlementType) stats.settlementTypes.add(settlementType);
      stats.storeIds.add(storeId ?? 'unknown');
      stats.customerIds.add(memberId ?? 'unknown');
    });

    return stats;
  }, [selectedOrders]);

  const hasStoreConflict = orderStats.storeIds.size > 1;
  const hasCustomerConflict = orderStats.customerIds.size > 1;

  // 计算默认表单值，避免在 useEffect 中重复计算
  const defaultFormValues = useMemo(() => {
    const defaultSettlementType =
      selectedOrders[0]?.omsRecycleOrder?.settlementType ?? 2;
    return {
      settlementType: defaultSettlementType,
      isOrderCompleted: 1,
    };
  }, [selectedOrders]);

  // 预处理订单展示数据，避免在渲染时重复计算
  const orderDisplayData = useMemo(() => {
    return selectedOrders.map((order) => {
      const id = order?.omsRecycleOrder?.id;
      const { productId } = order?.omsRecycleOrderItem || {};
      const orderType = order?.omsRecycleOrder?.type;
      const submissionId = order?.omsRecycleOrder?.submissionId;
      const orderTypeLabel = OrderType.find(
        (d) => d.value === orderType,
      )?.label;
      const currency = order?.omsRecycleOrder?.currency;
      const amount = getOrderAmount(order);
      const amountText =
        amount > 0 ? `${currency || ''} ${thousands(amount)}` : '-';

      return {
        key: id || productId,
        submissionId: submissionId || '-',
        orderTypeLabel: orderTypeLabel || '-',
        productId: productId || '-',
        amountText,
      };
    });
  }, [selectedOrders]);

  const totalAmountText = useMemo(() => {
    if (!selectedOrders.length) return '-';
    let total = 0;
    let currency = '';
    selectedOrders.forEach((order) => {
      total += getOrderAmount(order);
      if (!currency) {
        currency = order?.omsRecycleOrder?.currency || '';
      }
    });
    return `${currency} ${thousands(total)}`;
  }, [selectedOrders]);

  const handleVoucherChange = useCallback(
    (info: UploadChangeParam<UploadFile>) => {
      // 允许多个文件，不再限制为最后一个
      const normalized = info.fileList.map((file) =>
        file.originFileObj ? { ...file, status: 'done' as const } : file,
      );
      setVoucherFileList(normalized);
    },
    [],
  );

  const handleVoucherPreview = useCallback(async (file: UploadFile) => {
    if (file.url) {
      window.open(file.url);
      return;
    }
    if (file.originFileObj) {
      const src = await getBase64(file.originFileObj as RcFile);
      const image = new Image();
      image.src = src;
      const imgWindow = window.open(src);
      imgWindow?.document.write(image.outerHTML);
    }
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    form.resetFields();
    setVoucherFileList([]);
  }, [form, onClose]);

  // 统一验证逻辑，集中所有提交前的检查
  const validateSubmission = useCallback((): {
    valid: boolean;
    message?: string;
  } => {
    if (hasStoreConflict) {
      return {
        valid: false,
        message: i18n.t(LOCALS.order_store_diff_submit_error) as string,
      };
    }
    if (hasCustomerConflict) {
      return {
        valid: false,
        message: i18n.t(LOCALS.order_customer_diff_submit_error) as string,
      };
    }
    if (!selectedOrderIds.length) {
      return {
        valid: false,
        message: i18n.t(LOCALS.please_select_orders) as string,
      };
    }
    return { valid: true };
  }, [hasStoreConflict, hasCustomerConflict, selectedOrderIds.length]);

  const onFinish = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const validation = validateSubmission();
      if (!validation.valid) {
        message.warning(validation.message!);
        return;
      }

      // 立即设置 loading 状态，防止重复提交
      setLoading(true);

      const { settlementType, paymentAccount, isOrderCompleted } = values;
      const formData = new FormData();
      formData.append('ids', selectedOrderIds.join(','));
      formData.append('settlementType', String(settlementType));
      formData.append('isOrderCompleted', String(isOrderCompleted));
      if (paymentAccount) {
        formData.append('paymentAccount', paymentAccount);
      }

      // 获取所有新上传的图片文件
      const imageFiles = voucherFileList
        .filter((file) => file.originFileObj instanceof Blob)
        .map((file) => file.originFileObj as Blob);

      if (imageFiles.length > 0) {
        try {
          // 如果有多张图片，合成为一张
          const mergedBlob = await mergeImages(imageFiles);
          const mergedFile = new File(
            [mergedBlob],
            `batch-payment-voucher-${Date.now()}.jpg`,
            { type: 'image/jpeg' },
          );
          formData.append('file', mergedFile);

          if (imageFiles.length > 1) {
            message.success(`已将 ${imageFiles.length} 张图片合成为一张`);
          }
        } catch (error) {
          message.error('图片合成失败，请重试');
          setLoading(false);
          return;
        }
      }
      batchFetchPaymentVoucher(formData)
        .then(() => {
          message.success(i18n.t(LOCALS.submission_success));
          onSuccess?.();
          handleClose();
        })
        .catch(() => {
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      // Form validation failed
    }
  }, [
    form,
    handleClose,
    onSuccess,
    selectedOrderIds,
    validateSubmission,
    voucherFileList,
  ]);

  // 简化表单初始化，使用预计算的默认值
  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(defaultFormValues);
  }, [form, open, defaultFormValues]);

  return (
    <Modal
      open={open}
      title={i18n.t(LOCALS.batch_payment_voucher_entry)}
      onCancel={handleClose}
      onOk={onFinish}
      destroyOnClose
      confirmLoading={loading}
      width={640}
      className={styles.BatchPaymentVoucherModal}
      footer={[
        <Button onClick={handleClose} key="cancel">
          {i18n.t(LOCALS.cancel)}
        </Button>,
        <Popconfirm
          title={i18n.t(LOCALS.caution)}
          description={
            <div className={styles.Popconfirm}>
              {i18n.t(LOCALS.confirm_submission)}
            </div>
          }
          onConfirm={onFinish}
          okText={i18n.t(LOCALS.confirm)}
          cancelText={i18n.t(LOCALS.cancel)}
          disabled={hasStoreConflict || hasCustomerConflict}
          key="ok"
        >
          <Button
            type="primary"
            loading={loading}
            disabled={hasStoreConflict || hasCustomerConflict}
          >
            {i18n.t(LOCALS.confirm)}
          </Button>
        </Popconfirm>,
      ]}
    >
      <div className={styles.selectedOrdersHeader}>
        <span>
          {i18n.t(LOCALS.selected_orders)}（{selectedOrders.length}）
        </span>
        <span className={styles.totalAmount}>
          {i18n.t(LOCALS.total_amount)}：{totalAmountText}
        </span>
      </div>
      {(orderStats.currencies.size > 1 ||
        orderStats.settlementTypes.size > 1) && (
        <Alert
          type="warning"
          showIcon
          className={styles.orderAlert}
          message={i18n.t(LOCALS.order_currency_settlement_diff_warning)}
        />
      )}
      {hasStoreConflict && (
        <Alert
          type="error"
          showIcon
          className={styles.orderAlert}
          message={i18n.t(LOCALS.order_store_diff_error)}
        />
      )}
      {hasCustomerConflict && (
        <Alert
          type="error"
          showIcon
          className={styles.orderAlert}
          message={i18n.t(LOCALS.order_customer_diff_error)}
        />
      )}
      <div className={styles.orderList}>
        <div className={styles.orderHeader}>
          <span>{i18n.t(LOCALS.sHXnOEVSrC)}</span>
          <span>{i18n.t(LOCALS.order_type)}</span>
          <span>{i18n.t(LOCALS.associated_product_id)}</span>
          <span>{i18n.t(LOCALS.pending_payment_amount)}</span>
        </div>
        {orderDisplayData.map((item) => (
          <div key={item.key} className={styles.orderRow}>
            <span className={styles.cell}>{item.submissionId}</span>
            <span className={styles.cell}>{item.orderTypeLabel}</span>
            <span className={styles.cell}>{item.productId}</span>
            <span className={`${styles.cell} ${styles.amount}`}>
              {item.amountText}
            </span>
          </div>
        ))}
      </div>

      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 15 }}
      >
        <Form.Item
          label={i18n.t(LOCALS.payment_method)}
          name="settlementType"
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Radio value={1}>{i18n.t(LOCALS.cash)}</Radio>
            <Radio value={2}>{i18n.t(LOCALS.accounting_settlement)}</Radio>
          </Radio.Group>
        </Form.Item>
        {selectedPaymentMethod === 2 && orderStats.currencies.size === 1 && (
          <Form.Item label="打款账户" name="paymentAccount">
            <Select
              placeholder="请选择打款账户"
              allowClear
              options={
                PAYMENT_ACCOUNT_OPTIONS[
                  Array.from(orderStats.currencies)[0] ?? ''
                ] ?? []
              }
            />
          </Form.Item>
        )}
        <Form.Item
          label={i18n.t(LOCALS.complete_order)}
          name="isOrderCompleted"
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Radio value={0}>{i18n.t(LOCALS.no)}</Radio>
            <Radio value={1}>{i18n.t(LOCALS.yes)}</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label={i18n.t(LOCALS.payment_voucher)}
          className={styles.uploadRow}
        >
          <Upload
            accept="image/*"
            listType="picture-card"
            beforeUpload={(file, fileList) => {
              // 返回 false 阻止自动上传，但不影响多选
              return false;
            }}
            fileList={voucherFileList}
            onChange={handleVoucherChange}
            onPreview={handleVoucherPreview}
            multiple
            showUploadList={{
              showPreviewIcon: true,
              showRemoveIcon: true,
            }}
          >
            <UploadOutlined />
          </Upload>
          <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 6 }}>
            支持上传多张图片，提交时会自动合成为一张；也可复制/粘贴图片上传
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BatchPaymentVoucherModal;
